import React, { useState, useContext, useMemo } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TextField,
  CircularProgress,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { AppContext } from '../../AppContext';
import { updateFormHeaderConfig } from '../../services/formheader.service';

const useStyles = makeStyles((theme) => ({
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  cardHeader: {
    padding: '14px 18px',
    backgroundColor: '#f0f0f0',
    borderBottom: '1px solid #e0e0e0',
  },
  cardHeaderTitle: {
    fontSize: '1.3rem',
    fontWeight: 700,
    color: 'var(--jmi-blue)',
    letterSpacing: '0.01em',
  },
  cardContent: {
    padding: 16,
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    '&:last-child': { paddingBottom: 16 },
  },
  searchField: {
    marginBottom: 12,
    width: '100%',
    maxWidth: 320,
    flexShrink: 0,
  },
  tableWrapper: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
  },
  cell: {
    padding: '2px 8px',
  },
  checkbox: {
    padding: 4,
  },
}));

export const FormHeaderConfig = ({ formHeaders }) => {
  const classes = useStyles();
  const { dispatch } = useContext(AppContext);
  const [rows, setRows] = useState(formHeaders);
  const [savingId, setSavingId] = useState(null);
  const [search, setSearch] = useState('');

  const notify = (message, variant = 'success') =>
    dispatch({ type: 'SET_SNACKBAR', snackbar: { isOpen: true, message, variant } });

  const handleToggle = async (formHeaderId, canEditOnCancel) => {
    setSavingId(formHeaderId);
    setRows((prev) => prev.map((fh) => (fh.formHeaderId === formHeaderId ? { ...fh, canEditOnCancel } : fh)));
    try {
      await updateFormHeaderConfig(formHeaderId, canEditOnCancel);
      notify('Form header updated');
    } catch {
      setRows((prev) =>
        prev.map((fh) => (fh.formHeaderId === formHeaderId ? { ...fh, canEditOnCancel: !canEditOnCancel } : fh))
      );
      notify('Failed to update form header', 'error');
    } finally {
      setSavingId(null);
    }
  };

  const filteredRows = useMemo(
    () => rows.filter((fh) => (fh.name || '').toLowerCase().includes(search.toLowerCase())),
    [rows, search]
  );

  return (
    <Card className={`${classes.card} border-left-green`} elevation={2}>
      <CardHeader
        title="Form Header Configuration"
        subheader="Per-form settings, e.g. which forms cancellation QA users can still edit before finalizing a canceled order"
        classes={{ root: classes.cardHeader, title: classes.cardHeaderTitle }}
      />

      <CardContent className={classes.cardContent}>
        <TextField
          className={classes.searchField}
          placeholder="Search form headers..."
          variant="outlined"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <TableContainer className={classes.tableWrapper}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell className={classes.cell}>Form Header</TableCell>
                <TableCell className={classes.cell}>Alias</TableCell>
                <TableCell className={classes.cell}>Description</TableCell>
                <TableCell className={classes.cell} align="center">Editable on Cancel</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.length === 0 && (
                <TableRow>
                  <TableCell className={classes.cell} colSpan={4} align="center">
                    <Typography variant="body2" color="textSecondary">
                      No form headers found.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {filteredRows.map((fh) => (
                <TableRow key={fh.formHeaderId}>
                  <TableCell className={classes.cell}>{fh.name}</TableCell>
                  <TableCell className={classes.cell}>{fh.alias}</TableCell>
                  <TableCell className={classes.cell}>{fh.descript}</TableCell>
                  <TableCell className={classes.cell} align="center">
                    {savingId === fh.formHeaderId ? (
                      <CircularProgress size={20} />
                    ) : (
                      <Checkbox
                        className={classes.checkbox}
                        checked={!!fh.canEditOnCancel}
                        onChange={(e) => handleToggle(fh.formHeaderId, e.target.checked)}
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};
